import { useEffect, useState, useCallback, useRef } from "react";
import axiosInstance from "../api/apiClient";
import PageLayout from "../components/PageLayout";
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import update from 'immutability-helper';
import { HTML5Backend } from 'react-dnd-html5-backend';
import IconComponent from "../components/Icon";
import { FaPlus } from "react-icons/fa";
import { IoMdCloseCircle } from "react-icons/io";

const ItemTypes = { CARD: 'card' };

export const Card = ({ id, text, index, moveCard, book, onDelete }) => {
  const ref = useRef(null);

  const [, drop] = useDrop({
    accept: ItemTypes.CARD,
    hover(item, monitor) {
      if (!ref.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CARD,
    item: () => ({ id, index }),
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  });

  drag(drop(ref));
  return (
    <div
      ref={ref}
      style={{ opacity: isDragging ? 0 : 1 }}
      className="flex gap-4 items-center mb-4 relative w-[60%] bg-white rounded-3xl shadow-lg p-4 relative"
    >
      <img src={book['Cover URL']} alt={book.title} className="w-32 h-48 object-cover rounded-lg" />
      <div>
        <h3 className="text-lg font-bold">{book['Title']}</h3>
        <p className="text-sm text-gray-500">{book['Author']}</p>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <p className="trans-text text-2xl">{book.position}</p>
      </div>
      <IconComponent
        icon={IoMdCloseCircle}
        width={30}
        color="#60637A"
        className="absolute top-[-10px] right-[-10px] cursor-pointer"
        onClick={() => onDelete(book['Book Id'])}
      />
    </div>
  );
};

const Bookshelves = () => {
  const [booksInToBeRead, setBooksInToBeRead] = useState([]);
  const [searchString, setSearchString] = useState('');
  const [allUserBooks, setAllUserBooks] = useState([]);
  const [toBeReadList, setToBeReadList] = useState(null);
  const [showBooks, setShowBooks] = useState(false);
  const [toBeAddedBook, setToBeAddedBook] = useState(null);
  const [loading, setLoading] = useState(false);
  const dropDownRef = useRef(null);
  const bookshelfId = 621; // vervang indien nodig

  const getAllBooksInToBeReadList = useCallback(async (books) => {
    const booksData = [];
    await Promise.all(books.map(async book => {
      const r = await axiosInstance.get(`/getBook/${book.book_id}`);
      const b = r[0];
      b.position = book.position;
      booksData.push(b);
    }));
    booksData.sort((a, b) => a.position - b.position);
    setBooksInToBeRead(booksData);
  }, []);

  const getToBeReadList = useCallback(async () => {
    const r = await axiosInstance.get("/getUserBookshelves");
    const toRead = r.find(bs => bs.bookshelf_name === "to-be-read");
    setToBeReadList(toRead);
    if (toRead?.books) {
      await getAllBooksInToBeReadList(toRead.books);
    }
  }, [getAllBooksInToBeReadList]);

  const getUserBooks = useCallback(async () => {
    const r = await axiosInstance.get("/getUserBooks");
    setAllUserBooks(r);
  }, []);

  useEffect(() => {
    getToBeReadList();
    getUserBooks();

    const handleClickOutside = event => {
      if (dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowBooks(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [getToBeReadList, getUserBooks]);

  const moveCard = useCallback((dragIndex, hoverIndex) => {
    setBooksInToBeRead(prev => {
      const updated = update(prev, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, prev[dragIndex]],
        ],
      });

      const withPos = updated.map((book, i) => ({
        ...book,
        position: i + 1,
      }));

      setBooksInToBeRead(withPos);
      updateBookPositions(withPos);
      return withPos;
    });
  }, []);

  const updateBookPositions = async (books) => {
    try {
      const payload = books.map(b => ({
        book_id: b['Book Id'],
        bookshelf_id: bookshelfId,
        position: b.position,
      }));
      await axiosInstance.post('/updateBookPositions', payload);
    } catch (e) {
      console.error("Error updating positions:", e);
    }
  };

  const handleDeleteBook = async (bookIdToDelete) => {
    try {
      await axiosInstance.post('/deleteBookFromShelf', {
        book_id: bookIdToDelete,
        bookshelf_id: bookshelfId,
      });

      // Filter uit de frontend lijst
      const updatedBooks = booksInToBeRead
        .filter(b => b['Book Id'] !== bookIdToDelete)
        .map((b, i) => ({ ...b, position: i + 1 }));

      setBooksInToBeRead(updatedBooks);
      await updateBookPositions(updatedBooks);
    } catch (e) {
      console.error("Error deleting book:", e);
    }
  };

  const renderCard = useCallback((card, idx) => (
    <Card
      key={card['Book Id']}
      id={card['Book Id']}
      index={idx}
      moveCard={moveCard}
      book={card}
      onDelete={handleDeleteBook}
    />
  ), [moveCard, handleDeleteBook]);

  const addToToBeReadList = async () => {
    setLoading(true);
    try {
      const body = {
        book_id: toBeAddedBook['Book Id'],
        bookshelf_id: bookshelfId,
        position: booksInToBeRead.length + 1,
      };
      await axiosInstance.post('/addBookToShelf', body);
      await getToBeReadList();
      setToBeAddedBook(null);
      setSearchString('');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout headerText="YOUR TO BE READ LIST">
      <DndProvider backend={HTML5Backend}>
        <div className="flex flex-col w-full items-center">
          {booksInToBeRead.map((card, i) => renderCard(card, i))}

          {/* Add-book card */}
          <div className="flex gap-4 mb-4 relative w-[60%] bg-white rounded-3xl shadow-lg p-4">
            <div className="relative">
              {toBeAddedBook ? (
                <img
                  src={toBeAddedBook['Cover URL']}
                  alt={toBeAddedBook.title}
                  className="w-32 h-48 object-cover rounded-lg"
                />
              ) : (
                <div className="w-32 h-48 bg-[var(--dark-grey)] rounded-lg flex items-center justify-center">
                  <IconComponent icon={FaPlus} width={30} color="#fff" />
                </div>
              )}
            </div>
            <div className="flex flex-col flex-1">
              <h3 className="text-lg font-bold">Add book</h3>
              <div className="relative group" ref={dropDownRef}>
                <input
                  className="inline-flex justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchString}
                  placeholder="Search book"
                  onChange={e => setSearchString(e.target.value)}
                  onClick={() => setShowBooks(true)}
                />
                {showBooks && (
                  <div className="absolute w-full mt-2 bg-white border rounded shadow-lg max-h-60 overflow-auto z-10">
                    {allUserBooks.filter(book =>
                      Object.entries(book)
                        .filter(([k]) => !['Genres', 'Date Read', 'Date Added'].includes(k))
                        .some(([_, v]) => String(v).toLowerCase().includes(searchString.toLowerCase()))
                    ).map((book, idx) => (
                      <div
                        key={idx}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setToBeAddedBook(book);
                          setSearchString(book['Title']);
                          setShowBooks(false);
                        }}
                      >
                        {book['Title']} — {book['Author']}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button
                className={`mt-4 bg-[#8784D8] text-white font-bold py-2 px-4 rounded disabled:opacity-50`}
                disabled={!toBeAddedBook || loading}
                onClick={addToToBeReadList}
              >
                {loading ? 'Adding…' : 'Add to TBR'}
              </button>
            </div>
          </div>
        </div>
      </DndProvider>
    </PageLayout>
  );
};

export default Bookshelves;

